import React, { useState, useEffect, useRef } from 'react';
import { Heart, ShoppingCart, User, X, Minus, Plus, ChevronDown, ArrowLeft, AlertTriangle, CheckCircle } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Layout.css';
import logo from "../assets/images/logo.webp";
import { apiService } from '../api/api';
import product1 from '../assets/images/product1.jpg';


import Home from '../Pages/Home';
import AboutPage from '../Pages/About.jsx';
import ProductsPage from '../Pages/Products';
import ProductDetailPage from '../Pages/ProductDetail';
import ContactPage from '../Pages/Contact';
import PrivacyPage from '../Pages/Privacy';
import TermsPage from '../Pages/Terms';
import LoginPage from '../Pages/Login';
import ForgotPasswordPage from '../Pages/ForgotPassword';
import RegisterPage from '../Pages/Register';
import ProfilePage from '../Pages/ProfilePage.jsx';
import AdminDashboard from '../Pages/AdminDashboard';
import CheckoutPage from '../Pages/Checkout';

// ========================================
// PRICE UTILITY FUNCTIONS
// ========================================
export const parsePrice = (priceString) => {
  if (priceString === null || priceString === undefined || priceString === '') return null;
  if (typeof priceString === 'string') {
    const cleaned = priceString.replace(/[$,]/g, '');
    const parsed = parseFloat(cleaned);
    return (!isNaN(parsed) && parsed > 0) ? parsed : null;
  }
  const parsed = parseFloat(priceString);
  return (!isNaN(parsed) && parsed > 0) ? parsed : null;
};

export const formatPrice = (value) => {
  if (value === null || value === undefined || isNaN(value) || value <= 0) return 'N/A';
  return `$${value.toFixed(2)}`;
};

export const calculateSubtotal = (cartItems) => {
  let total = 0;
  cartItems.forEach(item => {
    const price = parsePrice(item.price);
    if (price !== null) total += price * item.quantity;
  });
  return total;
};

const isPriceNA = (item) =>
  item.price === 'N/A' ||
  item.unitPrice === null ||
  item.unitPrice === undefined ||
  parsePrice(item.price) === null;

const isGenericProductName = (s) => /^product\s*\d*$/i.test((s || '').trim());

// ========================================
// PRICE CHANGE MODAL COMPONENT
// ========================================
const PriceChangeModal = ({ isOpen, changes, onAccept, onCancel }) => {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
      @keyframes slideUp { from { opacity:0; transform: translateY(20px) } to { opacity:1; transform: translateY(0) } }
      @keyframes spin { to { transform: rotate(360deg) } }
      .pcm-cancel-btn:hover { background: #f9fafb !important; border-color: #9ca3af !important; }
      .pcm-accept-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(5,150,105,0.45) !important; }
    `;
    style.id = 'price-modal-keyframes';
    if (!document.getElementById('price-modal-keyframes')) document.head.appendChild(style);
    return () => { const el = document.getElementById('price-modal-keyframes'); if (el) el.remove(); };
  }, []);

  if (!isOpen) return null;

  const s = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(10,20,10,0.72)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', animation: 'fadeIn 0.2s ease' },
    modal: { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '540px', boxShadow: '0 24px 64px rgba(0,0,0,0.22)', overflow: 'hidden', animation: 'slideUp 0.25s ease' },
    header: { background: 'linear-gradient(135deg,#fff7ed,#fef3c7)', padding: '1.5rem 1.75rem 1.25rem', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'flex-start', gap: '0.875rem' },
    iconWrap: { background: '#f59e0b', borderRadius: '10px', padding: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    title: { margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#92400e', lineHeight: 1.3 },
    subtitle: { margin: '0.25rem 0 0', fontSize: '0.82rem', color: '#b45309' },
    body: { padding: '1.25rem 1.75rem', maxHeight: '340px', overflowY: 'auto' },
    item: { display: 'flex', alignItems: 'center', gap: '0.875rem', padding: '0.875rem', borderRadius: '10px', background: '#f9fafb', border: '1px solid #e5e7eb', marginBottom: '0.625rem' },
    img: { width: '52px', height: '52px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0, border: '1px solid #e5e7eb' },
    name: { fontWeight: 600, fontSize: '0.87rem', color: '#111827', marginBottom: '0.35rem', lineHeight: 1.3 },
    footer: { padding: '1rem 1.75rem 1.5rem', borderTop: '1px solid #f3f4f6', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' },
    btnCancel: { padding: '0.6rem 1.25rem', borderRadius: '8px', border: '1.5px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' },
    btnAccept: { padding: '0.6rem 1.4rem', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#059669,#047857)', color: '#fff', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', boxShadow: '0 2px 8px rgba(5,150,105,0.35)', transition: 'all 0.15s' },
  };

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <div style={s.header}>
          <div style={s.iconWrap}><AlertTriangle size={20} color="#fff" /></div>
          <div>
            <h5 style={s.title}>Price Changes Detected</h5>
            <p style={s.subtitle}>
              {changes.length} item{changes.length > 1 ? 's have' : ' has'} been updated since you added {changes.length > 1 ? 'them' : 'it'} to your cart.
            </p>
          </div>
        </div>

        <div style={s.body}>
          {changes.map((change, i) => (
            <div key={i} style={s.item}>
              <img src={change.image} alt={change.name} style={s.img} onError={(e) => { e.target.style.display = 'none'; }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={s.name}>{change.name}</p>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '0.35rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '0.68rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cart price</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#9ca3af', textDecoration: 'line-through' }}>
                      {change.oldPrice !== null ? `$${parseFloat(change.oldPrice).toFixed(2)}` : 'N/A'}
                    </span>
                  </div>
                  <span style={{ color: '#d1d5db', fontSize: '1rem', paddingBottom: '2px' }}>→</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '0.68rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current price</span>
                    {change.newPrice !== null
                      ? <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#059669', background: '#ecfdf5', padding: '1px 7px', borderRadius: '4px' }}>${parseFloat(change.newPrice).toFixed(2)}</span>
                      : <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#dc2626', background: '#fef2f2', padding: '1px 7px', borderRadius: '4px' }}>N/A</span>
                    }
                  </div>
                  {change.oldPrice !== null && change.newPrice !== null && (
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 7px', borderRadius: '4px', background: change.newPrice > change.oldPrice ? '#fef2f2' : '#ecfdf5', color: change.newPrice > change.oldPrice ? '#dc2626' : '#059669' }}>
                      {change.newPrice > change.oldPrice ? '▲ Increased' : '▼ Decreased'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={s.footer}>
          <button style={s.btnCancel} className="pcm-cancel-btn" onClick={onCancel}>Cancel</button>
          <button style={s.btnAccept} className="pcm-accept-btn" onClick={onAccept}>
            <CheckCircle size={16} /> Update &amp; Continue
          </button>
        </div>
      </div>
    </div>
  );
};

// ========================================
// CART SIDEBAR COMPONENT
// ========================================
const CartSidebar = ({
  isOpen,
  onClose,
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
  onContinueShopping,
  isValidatingPrices,
}) => {
  const subtotal = cartItems.reduce((sum, item) => {
    const price = parsePrice(item.price);
    return price !== null ? sum + price * item.quantity : sum;
  }, 0);

  return (
    <>
      {isOpen && <div className="overlay" onClick={onClose} />}
      <div className={`cart-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="cart-header">
          <div className="cart-title-section">
            <button
              onClick={onContinueShopping}
              className="cart-back-btn"
              aria-label="Back to products"
            >
              <ArrowLeft size={20} />
            </button>
            <h3>SHOPPING CART</h3>
            <span className="cart-count-badge">{cartItems.length}</span>
          </div>
          <button onClick={onClose} className="close-btn">
            <X size={24} />
          </button>
        </div>

        <div className="cart-items">
          {cartItems.length === 0 ? (
            <div className="empty-cart">
              <ShoppingCart size={48} />
              <p>Your cart is empty</p>
              <button className="continue-shopping-btn" onClick={onContinueShopping}>
                Browse Products
              </button>
            </div>
          ) : (
            cartItems.map((item) => (
              <div key={item.cartItemId || item.id} className="cart-item">
                <img
                  src={item.image || product1}
                  alt={item.name}
                  className="cart-item-image"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = product1;
                  }}
                />
                <div className="cart-item-details">
                  <h4>{item.name}</h4>
                  <div className="cart-item-controls">

                    {/* ── Quantity Controls with input ── */}
                    <div
                      className="quantity-controls"
                      style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      <button
                        onClick={() =>
                          onUpdateQuantity(item.cartItemId || item.id, item.quantity - 1)
                        }
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 6,
                          border: '1.5px solid #ddd',
                          background: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      >
                        <Minus size={14} />
                      </button>

                      <input
                        type="number"
                        min={1}
                        max={9999}
                        value={item.quantity}
                        onChange={(e) => {
                          const raw = e.target.value;
                          // Allow empty while typing
                          if (raw === '') return;
                          const val = parseInt(raw, 10);
                          if (!isNaN(val) && val >= 1 && val <= 9999) {
                            onUpdateQuantity(item.cartItemId || item.id, val);
                          }
                        }}
                        onBlur={(e) => {
                          // Snap back to 1 if left empty or invalid
                          const val = parseInt(e.target.value, 10);
                          if (isNaN(val) || val < 1) {
                            onUpdateQuantity(item.cartItemId || item.id, 1);
                          }
                        }}
                        style={{
                          width: 52,
                          height: 28,
                          textAlign: 'center',
                          border: '1.5px solid #ddd',
                          borderRadius: 6,
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#333',
                          outline: 'none',
                          // hide browser spin arrows
                          MozAppearance: 'textfield',
                        }}
                        // Hide webkit spin arrows via inline onFocus trick
                        onFocus={(e) => e.target.select()}
                      />

                      <button
                        onClick={() =>
                          onUpdateQuantity(item.cartItemId || item.id, item.quantity + 1)
                        }
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 6,
                          border: '1.5px solid #ddd',
                          background: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      >
                        <Plus size={14} />
                      </button>
                    </div>

                    {isPriceNA(item) ? (
                      <div className="cart-item-price-na">
                        <span className="price-na-text">Price: N/A</span>
                        <span className="request-price-text">Request for Price</span>
                      </div>
                    ) : (
                      <span className="cart-item-price">{item.price}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => onRemoveItem(item.cartItemId || item.id)}
                  className="remove-item"
                >
                  <X size={18} />
                </button>
              </div>
            ))
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="cart-footer">
            <div className="subtotal">
              <span>SUBTOTAL:</span>
              <span className="subtotal-amount">${subtotal.toFixed(2)}</span>
            </div>
            <button className="continue-shopping-btn-footer" onClick={onContinueShopping}>
              ← Continue Shopping
            </button>
            <button
              className="checkout-btn"
              onClick={onCheckout}
              disabled={isValidatingPrices}
              style={{
                opacity: isValidatingPrices ? 0.8 : 1,
                cursor: isValidatingPrices ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {isValidatingPrices ? (
                <>
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      display: 'inline-block',
                      animation: 'spin 0.7s linear infinite',
                    }}
                  />
                  Verifying…
                </>
              ) : (
                'CHECKOUT'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Hide number input spin arrows globally */}
      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>
    </>
  );
};

// ========================================
// WISHLIST SIDEBAR COMPONENT
// ========================================
const WishlistSidebar = ({ isOpen, onClose, wishlistItems, onRemoveItem, onAddToCart }) => (
  <>
    {isOpen && <div className="overlay" onClick={onClose} />}
    <div className={`cart-sidebar ${isOpen ? 'open' : ''}`}>
      <div className="cart-header">
        <div className="cart-title-section">
          <h3>WISHLIST</h3>
          <span className="cart-count-badge">{wishlistItems.length}</span>
        </div>
        <button onClick={onClose} className="close-btn"><X size={24} /></button>
      </div>
      <div className="cart-items">
        {wishlistItems.length === 0 ? (
          <div className="empty-cart"><Heart size={48} /><p>Your wishlist is empty</p></div>
        ) : (
          wishlistItems.map(item => (
            <div key={item.id} className="cart-item">
              <img src={item.image} alt={item.name} className="cart-item-image" />
              <div className="cart-item-details">
                <h4>{item.name}</h4>
                <span className="cart-item-price">{item.price}</span>
                <button className="add-to-cart-from-wishlist" onClick={() => onAddToCart(item)}>Add to Cart</button>
              </div>
              <button onClick={() => onRemoveItem(item.id)} className="remove-item"><X size={18} /></button>
            </div>
          ))
        )}
      </div>
    </div>
  </>
);

// ========================================
// HEADER COMPONENT
// ========================================
const Header = ({ onNavigate, activePage, cartCount, onCartClick, isLoggedIn, onCartClose }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);
  const profileDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && hamburgerRef.current && !hamburgerRef.current.contains(event.target)) setIsMenuOpen(false);
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) setIsProfileDropdownOpen(false);
    };
    if (isMenuOpen || isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => { document.removeEventListener('mousedown', handleClickOutside); document.removeEventListener('touchstart', handleClickOutside); };
  }, [isMenuOpen, isProfileDropdownOpen]);

  const handleNavClick = (page) => {
    onCartClose();  // ← close cart on any nav click
    onNavigate(page);
    setIsMenuOpen(false);
    setIsProfileDropdownOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header className="header">
      <div className="header-container">
        <nav className="navbar">
          <div className="logo"><img src={logo} alt="Orthomed Logo" className="logo-img" /></div>
          <button ref={hamburgerRef} className={`hamburger ${isMenuOpen ? 'active' : ''}`} onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu">
            <span></span><span></span><span></span>
          </button>
          <ul ref={menuRef} className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
            <li className="nav-item"><button onClick={() => handleNavClick('home')} className={`nav-link ${activePage === 'home' ? 'active' : ''}`}>Home</button></li>
            <li className="nav-item"><button onClick={() => handleNavClick('about')} className={`nav-link ${activePage === 'about' ? 'active' : ''}`}>About</button></li>
            <li className="nav-item"><button onClick={() => handleNavClick('products')} className={`nav-link ${activePage === 'products' ? 'active' : ''}`}>Products</button></li>
            <li className="nav-item"><button onClick={() => handleNavClick('contact')} className={`nav-link ${activePage === 'contact' ? 'active' : ''}`}>Contact Us</button></li>
            <li className="nav-item"><button onClick={() => handleNavClick('privacy')} className={`nav-link ${activePage === 'privacy' ? 'active' : ''}`}>Privacy Policy</button></li>
            <li className="nav-item"><button onClick={() => handleNavClick('terms')} className={`nav-link ${activePage === 'terms' ? 'active' : ''}`}>Terms and Conditions</button></li>
          </ul>
          <div className="header-icons">
            {isLoggedIn && (
              <button className="icon-btn" onClick={onCartClick} aria-label="Cart">
                <ShoppingCart size={24} />
                {cartCount > 0 && <span className="icon-badge">{cartCount}</span>}
              </button>
            )}
            {isLoggedIn ? (
              <div className="profile-dropdown" ref={profileDropdownRef}>
                <button className="icon-btn profile-btn" onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)} aria-label="Profile">
                  <User size={24} /><ChevronDown size={16} className="dropdown-arrow" />
                </button>
                {isProfileDropdownOpen && (
                  <div className="dropdown-menu show">
                    <button onClick={() => handleNavClick('profile')} className="dropdown-item">My Profile</button>
                    <div className="dropdown-divider"></div>
                    <button onClick={() => handleNavClick('logout')} className="dropdown-item logout-item">Logout</button>
                  </div>
                )}
              </div>
            ) : (
              <button onClick={() => handleNavClick('login')} className="login-button">Login</button>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

// ========================================
// FOOTER COMPONENT
// ========================================
const Footer = ({ onNavigate }) => {
  const handleNavClick = (page) => { onNavigate(page); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-header">
          <div className="row align-items-center">
            <div className="col-md-1"><img src={logo} alt="Orthomed Products" className="footer-logo" /></div>
            <div className="col-md-11">
              <nav className="footer-nav">
                {['home', 'about', 'products', 'contact', 'privacy', 'terms'].map(p => (
                  <a key={p} href={`#${p}`} onClick={(e) => { e.preventDefault(); handleNavClick(p); }} className="footer-nav-link">
                    {p === 'privacy' ? 'Privacy Policy' : p === 'terms' ? 'Terms and Conditions' : p.charAt(0).toUpperCase() + p.slice(1)}{p === 'contact' ? ' Us' : ''}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </div>
        <div className="footer-top">
          <div className="row">
            <div className="col-lg-6 col-md-12">
              <div className="row">
                <div className="col-md-6 mb-4">
                  <div className="contact-item"><h6 className="contact-label">Contact Us</h6><p className="contact-value"><a href="tel:+17326518800" className="contact-link">732 651 8800</a></p></div>
                  <div className="contact-item mt-4"><h6 className="contact-label">Fax</h6><p className="contact-value"><a href="tel:+17326518801" className="contact-link">732 651 8801</a></p></div>
                </div>
                <div className="col-md-6 mb-4">
                  <div className="contact-item"><h6 className="contact-label">Email</h6><p className="contact-value"><a href="mailto:orthomedproducts@gmail.com" className="contact-email-link">orthomedproducts@gmail.com</a></p></div>
                  <div className="contact-item mt-4"><h6 className="contact-label">Address</h6><p className="contact-value">11 Elkins Rd East Brunswick, NJ 08816</p></div>
                </div>
              </div>
            </div>
            <div className="col-lg-6 col-md-12">
              <p className="footer-description">At Orthomed, we understand the demands on your time. That's why we're dedicated to making your procurement process as efficient as possible. We specialize in providing a comprehensive range of cutting-edge equipment and supplies to healthcare professionals in various fields.</p>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="row align-items-center">
            <div className="col-md-6 mb-2 mb-md-0"><p className="copyright mb-0">© 2026 Orthomed Products. All Rights Reserved</p></div>
            <div className="col-md-6 text-md-end"><a href="#privacy" onClick={(e) => { e.preventDefault(); handleNavClick('privacy'); }} className="footer-link">Privacy Policy</a></div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ========================================
// MAIN LAYOUT COMPONENT
// ========================================
const Layout = () => {

  const [activePage, setActivePage] = useState(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const tokenInUrl = params.get('token');
    const emailInUrl = params.get('email');
    const tokenInSession = sessionStorage.getItem('resetToken');
    const isResetSession = sessionStorage.getItem('isResetLink') === 'true';
    const isResetLink = isResetSession || path.includes('reset-password') || Boolean(tokenInUrl);
    if (isResetLink) {
      if (tokenInUrl && !tokenInSession) { sessionStorage.setItem('resetToken', tokenInUrl); sessionStorage.setItem('isResetLink', 'true'); }
      if (emailInUrl && !sessionStorage.getItem('resetEmail')) sessionStorage.setItem('resetEmail', emailInUrl);
      localStorage.setItem('activePage', 'forgotPassword');
      return 'forgotPassword';
    }
    return localStorage.getItem('activePage') || 'home';
  });
  useEffect(() => {
    const url = new URL(window.location);

    if (url.searchParams.has('session')) {
      url.searchParams.delete('session');
      window.history.replaceState({}, document.title, url.pathname);
    }
  }, []);
  const [navParams, setNavParams] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isWishlistOpen, setIsWishlistOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(() => localStorage.getItem('isLoggedIn') === 'true');
  const [cartItems, setCartItems] = useState(() => { try { return JSON.parse(localStorage.getItem('cartItems')) || []; } catch { return []; } });
  const [wishlistItems, setWishlistItems] = useState([]);
  const [priceChanges, setPriceChanges] = useState([]);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [isValidatingPrices, setIsValidatingPrices] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(false);

  useEffect(() => {
    const isResetSession = sessionStorage.getItem('isResetLink') === 'true';
    const params = new URLSearchParams(window.location.search);
    const tokenInUrl = params.get('token');
    const isReset = isResetSession || window.location.pathname.includes('reset-password') || Boolean(tokenInUrl);
    if (isReset) {
      if (window.location.pathname !== '/' || tokenInUrl) window.history.replaceState({}, document.title, '/');
      setActivePage('forgotPassword');
      localStorage.setItem('activePage', 'forgotPassword');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { localStorage.setItem('cartItems', JSON.stringify(cartItems)); }, [cartItems]);

  useEffect(() => {
    if (!pendingCheckout) return;
    setPendingCheckout(false);
    setIsCartOpen(false);
    setActivePage('checkout');
    localStorage.setItem('activePage', 'checkout');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pendingCheckout]); // eslint-disable-line react-hooks/exhaustive-deps

  const transformCartItems = (backendCartItems) => {
    if (!Array.isArray(backendCartItems) || backendCartItems.length === 0) return [];
    let localSnapshot = [];
    try { localSnapshot = JSON.parse(localStorage.getItem('cartItems')) || []; } catch (_) { }
    const RAW_PLACEHOLDERS = new Set(['string', 'null', 'undefined', 'n/a', '']);
    const pickRaw = (...candidates) => { for (const c of candidates) { const s = (c || '').trim(); if (s && !RAW_PLACEHOLDERS.has(s.toLowerCase()) && !isGenericProductName(s)) return s; } return ''; };
    return backendCartItems.map((item) => {
      const product = item.product || item.productDetails || item.productInfo || {};
      const productId = item.productId || product.id || product.productId || 0;
      const rawEnriched = (item.productName || '').trim();
      const enrichedName = (RAW_PLACEHOLDERS.has(rawEnriched.toLowerCase()) || isGenericProductName(rawEnriched)) ? '' : rawEnriched;
      const rawName = pickRaw(item.productTitle, item.product_name, item.name, item.title, product.productName, product.productTitle, product.product_name, product.name, product.title, product.displayName, product.itemName);
      const localMatch = localSnapshot.find(lc => (lc.apiProductId != null && lc.apiProductId === productId) || (lc.id != null && lc.id === productId));
      const productName = enrichedName || rawName || pickRaw(localMatch?.name) || `Product ${productId}`;
      const rawUnitPrice = parseFloat(item.unitPrice) || parseFloat(product.unitPrice) || 0;
      const unitPrice = rawUnitPrice > 0 ? rawUnitPrice : null;
      const priceString = unitPrice !== null ? `$${unitPrice.toFixed(2)}` : 'N/A';
     const BACKEND = 'https://api.ecommerce.ashtamtechnologies.com/api/';
const toAbs = (url) => { if (!url) return null; return url.startsWith('http') ? url : `${BACKEND}/${url.replace(/^\//, '')}`; };

// Priority: imageUrlList → imageUrl → image → fallback
let imageUrl = null;
if (Array.isArray(product.imageUrlList) && product.imageUrlList.length > 0) {
  const first = product.imageUrlList.find(u => u);
  if (first) imageUrl = toAbs(first);
}
if (!imageUrl) imageUrl = toAbs(product.imageUrl || product.image || item.imageUrl || item.image || product.primaryImage);
if (!imageUrl) imageUrl = product1; // local fallback image
      return { id: productId, apiProductId: productId, cartItemId: item.id || item.cartItemId, name: productName, price: priceString, unitPrice, quantity: parseInt(item.quantity) || 1, image: imageUrl, description: product.description || item.description || '', category: product.category || item.category || 'N/A' };
    });
  };

const enrichCartItemsWithProductData = async (backendCartItems) => {
  let productData = [];
  try {
    const response = await apiService.getAllProductsForDisplay();
    if (response?.data && Array.isArray(response.data)) productData = response.data;
    else if (Array.isArray(response)) productData = response;
    else if (response?.message === 'Successfully' && response.data) productData = response.data;
  } catch (err) { console.warn('⚠️ enrich failed:', err.message); return backendCartItems; }
  if (productData.length === 0) return backendCartItems;

  const BACKEND = 'https://api.ecommerce.ashtamtechnologies.com/api/';
  const toAbs = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${BACKEND}/${url.replace(/^\//, '')}`;
  };

  // Get best image from a display product entry
  const getBestImage = (dp) => {
    if (!dp) return null;
    // imageUrlList is most reliable
    if (Array.isArray(dp.imageUrlList) && dp.imageUrlList.length > 0) {
      const first = dp.imageUrlList.find(u => u);
      if (first) return toAbs(first);
    }
    return toAbs(dp.imageUrl || dp.image) || null;
  };

  return backendCartItems.map((item) => {
    const productId = parseInt(item.productId || item.product?.id || 0, 10);
    if (!productId) return item;
    const match = productData[productId - 1];
    if (!match) return item;
    const realTitle = (match.title || match.name || '').trim();
    const bestImage = getBestImage(match);
    return {
      ...item,
      productName: realTitle || item.productName,
      product: {
        ...(item.product || {}),
        title:       realTitle          || item.product?.title,
        name:        realTitle          || item.product?.name,
        salePrice:   match.salePrice    ?? item.product?.salePrice,
        imageUrl:    bestImage          ?? item.product?.imageUrl,   // ← resolved absolute URL
        imageUrlList: match.imageUrlList ?? item.product?.imageUrlList ?? [],
        category:    match.category     ?? item.product?.category,
      },
    };
  });
};

  useEffect(() => {
    const fetchCart = async () => {
      if (!isLoggedIn) { localStorage.removeItem('cartId'); localStorage.removeItem('userId'); setCartItems([]); return; }
      const userId = localStorage.getItem('userId');
      if (!userId) return;
      try {
        const backendCartItems = await apiService.getUserCartWithItems(userId);
        if (Array.isArray(backendCartItems) && backendCartItems.length > 0) {
          const enriched = await enrichCartItemsWithProductData(backendCartItems);
          const transformed = transformCartItems(enriched);
          setCartItems(transformed);
          localStorage.setItem('cartItems', JSON.stringify(transformed));
        } else { setCartItems([]); }
      } catch (error) { console.error('❌ Failed to fetch cart:', error); }
    };
    fetchCart();
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavigate = (page, params = {}) => {
    if (page === 'logout') {
      apiService.logoutUser();
      return;
    }
    setNavParams(params);
    setActivePage(page);
    localStorage.setItem('activePage', page);
    if (page !== 'productDetail') {
      setSelectedProduct(null);
    }
  };

const handleLogin = () => {

  // ✅ CLEAN URL AFTER LOGIN
  window.history.replaceState({}, document.title, '/');

  const userRole = localStorage.getItem('userRole');

  if (userRole === 'admin') {
    setIsLoggedIn(true);
    setActivePage('admin');
    localStorage.setItem('activePage', 'admin');
  } else {
    setIsLoggedIn(true);
    localStorage.setItem('isLoggedIn', 'true');
    setActivePage('home');
    localStorage.setItem('activePage', 'home');
  }
};

  const handleLogout = () => {
    setIsLoggedIn(false);
    const cookiePrefs = localStorage.getItem('ortho_cookie_prefs');
    localStorage.clear();
    if (cookiePrefs) localStorage.setItem('ortho_cookie_prefs', cookiePrefs);
    setCartItems([]);
    setActivePage('login');
    localStorage.setItem('activePage', 'login');
  };

  const handleProductClick = (product) => { setSelectedProduct(product); setActivePage('productDetail'); localStorage.setItem('activePage', 'productDetail'); };
  const handleContactClick = () => { setActivePage('contact'); localStorage.setItem('activePage', 'contact'); };
  const handleContinueShopping = () => { setIsCartOpen(false); handleNavigate('products'); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const handleCartIconClick = () => { setIsCartOpen(true); handleNavigate('products'); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const validateCartPrices = async (currentCartItems) => {
    try {
      const response = await apiService.getAllProductsForDisplay();
      let productData = [];
      if (response?.data && Array.isArray(response.data)) productData = response.data;
      else if (Array.isArray(response)) productData = response;
      else if (response?.message === 'Successfully' && response.data) productData = response.data;
      if (productData.length === 0 || currentCartItems.length === 0) return [];
      const productByTitle = new Map();
      productData.forEach(p => { const t = (p.title || p.name || '').trim().toLowerCase(); if (t) productByTitle.set(t, p); });
      const changes = [];
      for (const cartItem of currentCartItems) {
        let apiProduct = null;
        const cartName = (cartItem.name || '').trim();
        const needle = cartName.toLowerCase();
        const isGeneric = /^product\s+\d+$/i.test(cartName);
        const apiId = parseInt(cartItem.apiProductId, 10);
        if (!isGeneric && needle) apiProduct = productByTitle.get(needle);
        if (!apiProduct && !isGeneric && needle.length >= 5) apiProduct = productData.find(p => { const t = (p.title || p.name || '').trim().toLowerCase(); return t.length >= 5 && (t.includes(needle) || needle.includes(t)); });
        if (!apiProduct && !isNaN(apiId) && apiId > 0 && apiId <= productData.length) apiProduct = productData[apiId - 1];
        if (!apiProduct) continue;
        let cartStoredPrice = null;
        if (cartItem.unitPrice !== null && cartItem.unitPrice !== undefined) { const p = parseFloat(cartItem.unitPrice); if (!isNaN(p) && p > 0) cartStoredPrice = p; }
        if (cartStoredPrice === null && cartItem.price && cartItem.price !== 'N/A') { const p = parseFloat(String(cartItem.price).replace(/[^0-9.]/g, '')); if (!isNaN(p) && p > 0) cartStoredPrice = p; }
        const liveRaw = apiProduct.salePrice ?? apiProduct.sale_price ?? apiProduct.price ?? apiProduct.unitPrice ?? apiProduct.sellingPrice ?? apiProduct.mrp ?? null;
        let currentLivePrice = null;
        if (liveRaw !== null && liveRaw !== undefined && liveRaw !== '') {
          const isVisibleDefined = apiProduct.isVisiblePrice !== undefined && apiProduct.isVisiblePrice !== null;
          if (!isVisibleDefined || Boolean(apiProduct.isVisiblePrice)) { const p = parseFloat(String(liveRaw).replace(/[^0-9.]/g, '')); if (!isNaN(p) && p > 0) currentLivePrice = p; }
        }
        const storedPaise = cartStoredPrice !== null ? Math.round(cartStoredPrice * 100) : null;
        const livePaise = currentLivePrice !== null ? Math.round(currentLivePrice * 100) : null;
        if (storedPaise !== livePaise) changes.push({ cartItemId: cartItem.cartItemId || cartItem.id, apiProductId: cartItem.apiProductId || apiProduct.id, name: cartItem.name, image: cartItem.image, oldPrice: cartStoredPrice, newPrice: currentLivePrice });
      }
      return changes;
    } catch (err) { console.error('❌ Price validation failed:', err); return []; }
  };

  const handleCheckout = async () => {
    let freshCart = cartItems;
    try { const stored = localStorage.getItem('cartItems'); if (stored) { const parsed = JSON.parse(stored); if (Array.isArray(parsed) && parsed.length > 0) freshCart = parsed; } } catch (_) { }
    if (freshCart.length === 0) return;

    setIsValidatingPrices(true);

    const changes = await validateCartPrices(freshCart);

    setIsValidatingPrices(false);

    if (changes.length === 0) {
      setIsCartOpen(false);
      handleNavigate('checkout');
    } else {
      setPriceChanges(changes);
      setShowPriceModal(true);
    }
  };

  const handleAcceptPriceChanges = async () => {
    const userId = localStorage.getItem('userId');
    const cartId = localStorage.getItem('cartId');
    const updatedCart = cartItems.map(item => {
      const change = priceChanges.find(c => c.cartItemId === (item.cartItemId || item.id));
      if (!change) return item;
      return { ...item, price: change.newPrice !== null ? `$${parseFloat(change.newPrice).toFixed(2)}` : 'N/A', unitPrice: change.newPrice };
    });
    localStorage.setItem('cartItems', JSON.stringify(updatedCart));
    if (isLoggedIn && userId) {
      await Promise.allSettled(priceChanges.map(async (change) => {
        const cartItem = updatedCart.find(i => (i.cartItemId || i.id) === change.cartItemId);
        if (!cartItem?.cartItemId) return;
        const newUnitPrice = change.newPrice ?? 0;
        try { await apiService.updateCartItem(cartItem.cartItemId, { cartId: parseInt(cartId) || 0, productId: cartItem.apiProductId || cartItem.id, quantity: cartItem.quantity, unitPrice: newUnitPrice, totalPrice: newUnitPrice * (cartItem.quantity || 1), cartStatus: 0 }); }
        catch (err) { console.error(`❌ Failed to sync price for "${change.name}":`, err); }
      }));
    }
    setPriceChanges([]);
    setShowPriceModal(false);
    setCartItems(updatedCart);
    setPendingCheckout(true);
  };

  const handleCancelPriceModal = () => { setPriceChanges([]); setShowPriceModal(false); };

  const handleAddToCart = async (product) => {
    const userId = localStorage.getItem('userId');
    let cartId = localStorage.getItem('cartId');
    let unitPrice = null, priceString = 'N/A';
    if (product.salePrice !== null && product.salePrice !== undefined) { const p = parseFloat(product.salePrice); if (!isNaN(p) && p > 0) { unitPrice = p; priceString = `$${p.toFixed(2)}`; } }
    else if (product.price !== null && product.price !== undefined) { const raw = typeof product.price === 'string' ? parseFloat(product.price.replace(/[$,]/g, '')) : parseFloat(product.price); if (!isNaN(raw) && raw > 0) { unitPrice = raw; priceString = `$${raw.toFixed(2)}`; } }
    const apiUnitPrice = unitPrice ?? 0;
    const existingIdx = cartItems.findIndex(item => item.apiProductId && product.apiProductId ? item.apiProductId === product.apiProductId : item.id === product.id);
if (existingIdx !== -1) {
  const updatedCart = [...cartItems];
  const newQty = updatedCart[existingIdx].quantity + 1;
  updatedCart[existingIdx].quantity = newQty;
  // If reorder passed a better image, update it
  if (product.image && product.image !== updatedCart[existingIdx].image) {
    updatedCart[existingIdx].image = product.image;
  }
  if (isLoggedIn && userId) {
    try { const cid = updatedCart[existingIdx].cartItemId; if (cid) await apiService.updateCartItem(cid, { cartId: parseInt(cartId) || 0, productId: product.apiProductId || product.id, quantity: newQty, unitPrice: apiUnitPrice, totalPrice: apiUnitPrice * newQty, cartStatus: 0 }); }
    catch (error) { console.error('❌ Update failed:', error); alert('Failed to update cart.'); return; }
  }
  setCartItems(updatedCart);
} else {
      if (isLoggedIn && userId) {
        try {
          let response;
          if (!cartId || cartId === 'null') { const cartInfo = await apiService.getUserCart(userId); if (cartInfo?.cartId) { cartId = cartInfo.cartId; localStorage.setItem('cartId', String(cartId)); } }
          if (!cartId || cartId === 'null') response = await apiService.addToCart({ productId: product.apiProductId || product.id, quantity: 1, unitPrice: apiUnitPrice, totalPrice: apiUnitPrice });
          else response = await apiService.addCartItem({ productId: product.apiProductId || product.id, quantity: 1, unitPrice: apiUnitPrice });
          setCartItems(prev => [...prev, { id: product.id, apiProductId: product.apiProductId || null, cartItemId: response?.id, name: product.name || product.title || `Product ${product.id}`, price: priceString, unitPrice, quantity: 1, image: product.image || product.imageUrl || '', description: product.description || '', category: product.category || 'N/A' }]);
        } catch (error) { console.error('❌ Add failed:', error); alert('Failed to add to cart.'); return; }
      } else {
        setCartItems(prev => [...prev, { id: product.id, apiProductId: product.apiProductId || null, name: product.name || product.title || `Product ${product.id}`, price: priceString, unitPrice, quantity: 1, image: product.image || product.imageUrl || '', description: product.description || '', category: product.category || 'N/A' }]);
      }
    }
    setIsCartOpen(true);
  };

  const handleUpdateQuantity = async (cartItemKey, newQuantity) => {
    if (newQuantity < 1) { handleRemoveFromCart(cartItemKey); return; }
    const userId = localStorage.getItem('userId');
    const cartId = localStorage.getItem('cartId');
    const cartItem = cartItems.find(item => (item.cartItemId || item.id) === cartItemKey);
    if (!cartItem) return;
    const apiUnitPrice = cartItem.unitPrice ?? 0;
    if (isLoggedIn && userId && cartItem.cartItemId) {
      try { await apiService.updateCartItem(cartItem.cartItemId, { cartId: parseInt(cartId) || 0, productId: cartItem.apiProductId || cartItem.id, quantity: newQuantity, unitPrice: apiUnitPrice }); }
      catch (error) { console.error('❌ Failed to update quantity:', error); alert('Failed to update quantity.'); return; }
    }
    setCartItems(prev => prev.map(item => (item.cartItemId || item.id) === cartItemKey ? { ...item, quantity: newQuantity } : item));
  };

  const handleRemoveFromCart = async (cartItemKey) => {
    const itemToRemove = cartItems.find(item => (item.cartItemId || item.id) === cartItemKey);
    if (isLoggedIn && itemToRemove?.cartItemId) { try { await apiService.deleteCartItem(itemToRemove.cartItemId); } catch (error) { console.error('❌ Delete failed:', error); } }
    setCartItems(prev => prev.filter(item => (item.cartItemId || item.id) !== cartItemKey));
  };

  const handleRemoveFromWishlist = (id) => setWishlistItems(prev => prev.filter(item => item.id !== id));
  const handleAddToCartFromWishlist = (item) => {
    const existing = cartItems.find(c => c.id === item.id);
    if (existing) handleUpdateQuantity(existing.cartItemId || existing.id, existing.quantity + 1);
    else handleAddToCart(item);
    handleRemoveFromWishlist(item.id);
  };

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const isAdminPage = activePage === 'admin';
  const handleOpenCart = () => setIsCartOpen(true);

  const renderPage = () => {
    switch (activePage) {
      case 'home': return <Home onNavigate={handleNavigate} />;
      case 'about': return <AboutPage onNavigate={handleNavigate} />;
      case 'products': return <ProductsPage onProductClick={handleProductClick} onNavigate={handleNavigate} />;
      case 'productDetail':
        return <ProductDetailPage selectedProduct={selectedProduct} onProductClick={handleProductClick} onContactClick={handleContactClick} onAddToCart={handleAddToCart} cartItems={cartItems} onUpdateQuantity={handleUpdateQuantity} onRemoveFromCart={handleRemoveFromCart} />;
      case 'contact': return <ContactPage onNavigate={handleNavigate} />;
      case 'privacy': return <PrivacyPage onNavigate={handleNavigate} />;
      case 'terms': return <TermsPage onNavigate={handleNavigate} />;
      case 'profile':
      case 'orders': return <ProfilePage onNavigate={handleNavigate} onAddToCart={handleAddToCart} onOpenCart={handleOpenCart} />;
      case 'checkout': return <CheckoutPage cartItems={cartItems} onNavigate={handleNavigate} isLoggedIn={isLoggedIn} onOrderComplete={() => { setCartItems([]); setIsCartOpen(false); }} />;
      case 'login': return <LoginPage onNavigate={handleNavigate} onLogin={handleLogin} />;
      case 'register': return <RegisterPage onNavigate={handleNavigate} />;
      case 'admin': return <AdminDashboard onNavigate={handleNavigate} onLogout={handleLogout} />;
      case 'forgotPassword': return <ForgotPasswordPage onNavigate={handleNavigate} initialEmail={navParams.email || ''} skipToStep={navParams.skipToStep || 1} />;
      default: return <Home />;
    }
  };

  return (
    <div className="layout-wrapper">
      {!isAdminPage && (
        <Header
          onNavigate={handleNavigate}
          activePage={activePage}
          cartCount={cartCount}
          onCartClick={handleCartIconClick}
          isLoggedIn={isLoggedIn}
          onCartClose={() => setIsCartOpen(false)}
        />
      )}

      <main>{renderPage()}</main>

      {!isAdminPage && <Footer onNavigate={handleNavigate} />}

      {!isAdminPage && isLoggedIn && (
        <>
          <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} cartItems={cartItems} onUpdateQuantity={handleUpdateQuantity} onRemoveItem={handleRemoveFromCart} onCheckout={handleCheckout} onContinueShopping={handleContinueShopping} isValidatingPrices={isValidatingPrices} />
          <WishlistSidebar isOpen={isWishlistOpen} onClose={() => setIsWishlistOpen(false)} wishlistItems={wishlistItems} onRemoveItem={handleRemoveFromWishlist} onAddToCart={handleAddToCartFromWishlist} />
          <PriceChangeModal isOpen={showPriceModal} changes={priceChanges} onAccept={handleAcceptPriceChanges} onCancel={handleCancelPriceModal} />
        </>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Layout;